import React, { useRef } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const utahData = "/img/UTDT_Logo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdf = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

export default function CsAndItTable24({ data }) {
  const pdfRef = useRef();

  if (!data?.length) {
    return <p className="text-center text-red-600">⚠ No data available</p>;
  }

  const years = [
    "2018-2019",
    "2019-2020",
    "2020-2021",
    "2021-2022",
    "2022-2023",
    "2023-2024",
  ];
  const colors = [
    "#B45309",
    "#10B981",
    "#374151",
    "#10B981",
    "#DFCCF1",
    "#9CA3AF",
  ];

  const datasets = data.map((group, i) => ({
    label: group.race_label,
    data: years.map((interval) =>
      Number(group[`yoy_${interval.replace("-", "_")}`]),
    ),
    borderColor: colors[i % colors.length],
    pointBorderColor: colors[i % colors.length],
    pointRadius: 6,
    backgroundColor: "transparent",
    borderWidth: 2,
    tension: 0.35,
  }));

  // Chart Config
  const chartData = { labels: years, datasets };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "Year-over-Year Employment Change by Race (2018-2024)",
        font: { size: 16, weight: "bold" },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: "Year-over-Year Interval",
          font: { size: 14, weight: "bold" },
        },
      },
      y: {
        beginAtZero: false,
        title: {
          display: true,
          text: "Employment Change",
          font: { size: 14, weight: "bold" },
        },
        ticks: { callback: (v) => v.toLocaleString("en-US") },
      },
    },
  };

  // ---------- Insights ----------
  const getMaxGroup = () => {
    let max = { race: "", value: -Infinity };
    data.forEach((d) => {
      const values = years.map((y) => Number(d[`yoy_${y.replace("-", "_")}`]));
      const total = values.reduce((a, b) => a + b, 0);
      if (total > max.value) max = { race: d.race_label, value: total };
    });
    return max;
  };

  const getMinGroup = () => {
    let min = { race: "", value: Infinity };
    data.forEach((d) => {
      const values = years.map((y) => Number(d[`yoy_${y.replace("-", "_")}`]));
      const total = values.reduce((a, b) => a + b, 0);
      if (total < min.value) min = { race: d.race_label, value: total };
    });
    return min;
  };

  const maxGroup = getMaxGroup();
  const minGroup = getMinGroup();

  // ---------- PDF Export ----------
  const downloadPDF = () => {
    const input = pdfRef.current;
    html2canvas(input, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - 10;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 1;

      const addHeader = () => {
        const logoWidth = 20; // ideal header width
        const logoHeight = 20; // adjust ratio if needed

        pdf.addImage(utahData, "PNG", 70, 10, logoWidth, logoHeight);
        pdf.setFontSize(26);
        pdf.text("UTahData.org", 95, 22);
        pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
        pdf.line(10, 35, 200, 35);
      };

      const addFooter = (pageNumber) => {
        pdf.setFontSize(9);
        const footerY = pageHeight - 10;
        pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          10,
          footerY,
        );
        pdf.text(
          `Page ${pageNumber}`,
          pageWidth - 10,
          footerY,
          null,
          null,
          "right",
        );
      };

      // First page
      addHeader();
      pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
      heightLeft -= pageHeight - 32;

      // Add more pages if needed
      while (heightLeft > 0) {
        pdf.addPage();
        pageNumber++;
        addHeader();
        position = heightLeft - imgHeight;
        pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;
        addFooter(pageNumber);
      }

      // Final footer
      // ===== FINAL FOOTER SAFE AREA =====

      const footerTopY = pageHeight - 40; // start footer 40mm from bottom
      const footerContentY = pageHeight - 28; // logos row
      const footerBottomTextY = pageHeight - 10; // bottom text row

      // Top Divider Line
      pdf.line(10, footerTopY, pageWidth - 10, footerTopY);

      // UtahData logo
      const utahWidth = 18;
      const utahHeight = 18;
      pdf.addImage(
        utahData,
        "PNG",
        20,
        footerContentY - 8,
        utahWidth,
        utahHeight,
      );

      // Built by
      pdf.setFontSize(10);
      pdf.text("Built by", 45, footerContentY);

      // BODEX logo
      const bodexWidth = 36;
      const bodexHeight = bodexWidth * 0.3;
      pdf.addImage(
        bodex,
        "PNG",
        60,
        footerContentY - 6,
        bodexWidth,
        bodexHeight,
      );

      // Using text
      pdf.text("using", 102, footerContentY);

      // KPI logo
      pdf.addImage(kpi, "PNG", 118, footerContentY - 8, 16, 16);

      // Version
      pdf.text("20250916.v3.198", 140, footerContentY);

      // Bottom Info Line
      pdf.setFontSize(8);
      pdf.text(`From: ${window.location.href}`, 10, footerBottomTextY);
      pdf.text(
        `Downloaded on: ${new Date().toLocaleDateString()}`,
        pageWidth - 10,
        footerBottomTextY,
        null,
        null,
        "right",
      );

      pdf.save("yoy_emp_trend_by_race.pdf");
    });
  };

  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white shadow-lg rounded-xl">
        <h2 className="text-2xl font-bold text-center mb-6">
          Year-over-Year Employment Change by Race (2018–2024)
        </h2>

        {/* Summary Boxes */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-center mb-6">
          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">Highest Total YoY Gain</p>
            <p className="text-xl font-bold text-green-800">
              {maxGroup.race}: +{maxGroup.value.toLocaleString("en-US")}
            </p>
          </div>
          <div className="p-4 bg-red-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">Largest Total YoY Loss</p>
            <p className="text-xl font-bold text-red-700">
              {minGroup.race}: {minGroup.value.toLocaleString("en-US")}
            </p>
          </div>
        </div>

        {/* Table + Chart Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Table */}
          <div className="flex flex-col justify-center">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2 text-left">Race</th>
                    {years.map((y) => (
                      <th
                        className="p-2"
                        key={y}
                        style={{ textAlign: "right" }}
                      >
                        {y}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, i) => (
                    <tr key={i} className="hover:bg-gray-100">
                      <td className="font-semibold p-2 text-left">
                        {row.race_label}
                      </td>
                      {years.map((y) => (
                        <td
                          key={y}
                          className={`font-bold p-2 ${
                            Number(row[`yoy_${y.replace("-", "_")}`]) >= 0
                              ? "text-blue-800"
                              : "text-red-600"
                          }`}
                          style={{
                            textAlign: "right",
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {Number(
                            row[`yoy_${y.replace("-", "_")}`],
                          ).toLocaleString("en-US")}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Line data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          Download as PDF
          <img src={pdf} alt="pdf" className="w-[1.5rem]" />
        </button>
      </div>
    </>
  );
}
